// mdsMethodHooks — the TS2425 escape hatch for native-class widgets over the faithful dijit/dojox
// declare() bases.
//
// mds-dojo's faithful declare() re-implementation types a class's members from the props OBJECT it
// was declared with, so lifecycle/reflow hooks (postCreate, startup, buildRendering, …) surface as
// function-VALUED PROPERTIES. TypeScript forbids overriding a property with a native METHOD
// (TS2425: "defines instance member property … but extended class defines it as instance member
// function"), and a mapped type cannot re-present a property as a method — so a `class X extends
// compose(dijit/_WidgetBase, …)` that overrides `postCreate()` as a method fails to typecheck.
//
// This wraps a compose() base so the known-overridden hooks are Omit-ted from the base instance
// type and re-added as METHOD signatures (method-over-method is allowed). It's the reusable form of
// the inline `_WBLBase` wrapper in the oracle's more/_WidgetBaseLite.ts. RUNTIME is untouched — this
// is a pure type-level cast (the value is returned unchanged).
//
// Apply it at every native↔faithful-declare boundary (a class extending compose(_WidgetBase, …) /
// compose(a dojox calendar base, …) directly). Classes that extend an app base which already went
// through it (e.g. compose(_WidgetBaseSP, …)) inherit method-typed hooks and need nothing.
import type { Ctor } from 'dojo/_base/declare';

// The union of every dijit/dojox/app hook overridden as a method somewhere in the app (kept in sync
// by grepping TS2425). Over-including a hook a given base does not declare is harmless: Omit of an
// absent key is a no-op, and the extra member is simply an available `(...): any` method.
interface MethodHooks {
  // dijit lifecycle
  postMixInProperties(...a: any[]): any;
  buildRendering(...a: any[]): any;
  postCreate(...a: any[]): any;
  startup(...a: any[]): any;
  destroy(...a: any[]): any;
  _stringRepl(...a: any[]): any;
  addChild(...a: any[]): any;
  // dojox calendar (renderers / views / calendar)
  updateRendering(...a: any[]): any;
  _isElementVisible(...a: any[]): any;
  onItemEditEndGesture(...a: any[]): any;
  matrixViewRowHeaderClick(...a: any[]): any;
  isItemEditable(...a: any[]): any;
  isItemMoveEnabled(...a: any[]): any;
  isItemResizeEnabled(...a: any[]): any;
  encodeDate(...a: any[]): any;
  decodeDate(...a: any[]): any;
  _createDefaultViews(...a: any[]): any;
  _configureView(...a: any[]): any;
  _computeCurrentView(...a: any[]): any;
}

/**
 * Re-type a `compose(...)` base so the dijit/dojox lifecycle + reflow hooks are METHODS (not
 * function-valued properties), so a native subclass can override them without TS2425. Runtime
 * identity — returns the same constructor.
 */
export function methodHooks<C extends Ctor>(Base: C): {
  new (...a: any[]): Omit<InstanceType<C>, keyof MethodHooks> & MethodHooks;
} {
  return Base as any;
}

export default methodHooks;
